import { MoreHorizontal } from 'lucide-react'
import { useMemo, useState } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { IconButton } from '@/components/ui/IconButton'

export interface BlockAction {
  id: string
  label: string
  onSelect: (id: string) => void
  /** Actions in different groups are separated by a divider, in declaration order. */
  group?: string
  disabled?: boolean
  /** When true, the action is also surfaced as an inline button in the hover toolbar
   *  (in addition to inside the dropdown). Use sparingly — 1–3 quick actions max. */
  quick?: boolean
}

interface Props {
  actions: BlockAction[]
  disabled?: boolean
  /** Called whenever the dropdown opens or closes — the BlockShell uses this to
   *  keep the floating toolbar visible while the menu is open. */
  onOpenChange?: (open: boolean) => void
}

export function BlockActionsMenu({ actions, disabled = false, onOpenChange }: Props) {
  const [open, setOpen] = useState(false)
  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    onOpenChange?.(next)
  }

  const groups = useMemo(() => {
    const out: BlockAction[][] = []
    let last: string | undefined
    for (const a of actions) {
      const grp = a.group ?? '__default'
      if (grp !== last) {
        out.push([])
        last = grp
      }
      out[out.length - 1].push(a)
    }
    return out
  }, [actions])

  const quickActions = useMemo(() => actions.filter((a) => a.quick), [actions])

  return (
    <div className="inline-flex items-center gap-1 rounded-md border border-border bg-background p-0.5 shadow-sm">
      {quickActions.map((a) => (
        <Button
          key={a.id}
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={disabled || a.disabled}
          onClick={() => a.onSelect(a.id)}
        >
          {a.label}
        </Button>
      ))}
      <DropdownMenu open={open} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <IconButton size="sm" aria-label="Open actions" disabled={disabled}>
            <MoreHorizontal aria-hidden />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {groups.map((group, i) => (
            <div key={i}>
              {i > 0 && <DropdownMenuSeparator />}
              {group.map((a) => (
                <DropdownMenuItem
                  key={a.id}
                  disabled={a.disabled}
                  onSelect={() => a.onSelect(a.id)}
                >
                  {a.label}
                </DropdownMenuItem>
              ))}
            </div>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
